import { useState } from "react";
import { Link } from "react-router-dom";
import { useTripPlan } from "@/hooks/useTripPlan";

interface TripBadgeProps {
  scrolled?: boolean;
  mobile?: boolean;
  onNavigate?: () => void;
}

/**
 * The floating "Your Trip (N stops)" badge shown in the navbar.
 * Tapping the badge returns to the planner; the little x opens a confirm
 * popover so visitors can reset their plan from anywhere on the site.
 */
export default function TripBadge({ scrolled = true, mobile = false, onNavigate }: TripBadgeProps) {
  const { totalStops, clearPlan } = useTripPlan();
  const [confirming, setConfirming] = useState(false);

  if (totalStops === 0) return null;

  const light = mobile || scrolled;
  const shellClass = light ? "text-primary-700 bg-primary-50" : "text-background-50 bg-background-50/15";
  const hoverClass = light ? "hover:bg-primary-100" : "hover:bg-background-50/25";
  const clearClass = light
    ? "text-primary-600 hover:text-primary-800 hover:bg-primary-100"
    : "text-background-200 hover:text-background-50 hover:bg-background-50/20";

  const closeConfirm = () => setConfirming(false);

  return (
    <div className={`relative ${mobile ? "w-full" : ""}`}>
      <div
        className={`${mobile ? "flex w-full" : "inline-flex"} items-center rounded-md transition-colors ${shellClass}`}
      >
        <Link
          to="/plan-your-trip"
          onClick={onNavigate}
          className={`${mobile ? "flex-1 flex items-center justify-between" : "inline-flex items-center"} gap-2 pl-3 pr-2 py-2 text-sm font-semibold whitespace-nowrap rounded-md transition-colors cursor-pointer ${hoverClass}`}
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-route-line text-base"></i>
            Your Trip
          </span>
          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-[11px] font-bold">
            {totalStops}
          </span>
        </Link>
        <button
          type="button"
          onClick={() => setConfirming((open) => !open)}
          aria-label="Clear your saved trip"
          aria-expanded={confirming}
          className={`w-8 h-8 mr-1 flex items-center justify-center rounded-md transition-colors cursor-pointer ${clearClass}`}
        >
          <i className={confirming ? "ri-close-fill text-base" : "ri-close-line text-base"}></i>
        </button>
      </div>

      {confirming && (
        <>
          <button
            type="button"
            aria-label="Cancel clearing your trip"
            onClick={closeConfirm}
            className="fixed inset-0 z-40 cursor-default"
          ></button>
          <div
            className={`absolute top-full mt-2 z-50 w-60 bg-background-50 rounded-md border border-background-200 p-4 text-left ${
              mobile ? "left-0" : "right-0"
            }`}
          >
            <p className="text-sm font-semibold text-foreground-950">Clear your trip?</p>
            <p className="text-xs text-foreground-600 leading-relaxed mt-1">
              This removes all {totalStops} {totalStops === 1 ? "stop" : "stops"} from your saved
              itinerary. You can always build a new one.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={() => {
                  clearPlan();
                  setConfirming(false);
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-primary-500 text-background-50 text-xs font-semibold px-3 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-delete-bin-line"></i>
                Clear trip
              </button>
              <button
                type="button"
                onClick={closeConfirm}
                className="flex-1 inline-flex items-center justify-center bg-background-100 text-foreground-800 border border-background-300 text-xs font-semibold px-3 py-2.5 rounded-md hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Keep it
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}